import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert } from 'shared/ui'
import { LINK_THROUGHPUT_MESSAGE_GUIDE } from 'shared/constants/links'
import { type IModalInfoRowProps } from 'shared/ui/Modal/ui/ModalInfoRow/ModalInfoRow'
import { ModalInfoBlock } from 'shared/ui/Modal/ui/ModalInfoBlock/ModalInfoBlock'
import { numberFormat } from 'shared/lib'
import type { ComplianceServiceBrand } from 'entities/Compliance/model'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ModalHeader } from 'pages/settings/pages/compliance/ui/ModalHeader/ModalHeader'
import type { ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import styles from './styles.module.scss'

type IBrandModalContentProps = {
  complianceLayerStore: ComplianceLayerStore
  brand: ComplianceServiceBrand
  statusData: ReactNode
  updateModalAction: () => void
  learnMoreModalAction: () => void
}

export const BrandModalContent = observer(
  ({
    complianceLayerStore,
    brand,
    statusData,
    updateModalAction,
    learnMoreModalAction,
  }: IBrandModalContentProps) => {
    const { businessInformation } = complianceLayerStore
    const lowVolumePlan = brand.plan_type === 'LOW_VOLUME_STANDARD'
    const { registrationError, isPublicType, vetting } = brand
    const { tenDlcLimitsStore } = useEntitiesContext()
    const { brandScoreSegmentsTo } = tenDlcLimitsStore
    const dailyLimitsStandardValue = numberFormat({
      value: typeof brandScoreSegmentsTo === 'number' ? brandScoreSegmentsTo : 600000,
    })

    const lines: IModalInfoRowProps[] = [
      {
        label: 'Name',
        value: businessInformation?.business_name || '',
      },
      {
        label: 'Package',
        value: lowVolumePlan ? 'Low volume standard' : 'Standard',
      },
      {
        label: 'Daily limits',
        value: lowVolumePlan
          ? 'up to 2,000 SMS / MMS segments'
          : `up to ${dailyLimitsStandardValue} SMS / MMS segments`,
      },
      {
        label: 'Company type',
        value: brand?.entityType || '',
      },
    ]

    const vettingScore = vetting?.score

    if (!lowVolumePlan) {
      lines.splice(1, 0, {
        label: 'Trust score',
        value: vettingScore || (
          <span className={styles.trustScore}>Will be determined upon brand approval</span>
        ),
        tooltip: [
          'Impacts the messaging throughput (MPS)',
          <a
            href={LINK_THROUGHPUT_MESSAGE_GUIDE}
            target='_blank'
            rel='noreferrer'
            className={styles.learnMorelink}
            key={'learnMore'}
          >
            Learn more
          </a>,
        ],
      })
    }

    if (isPublicType) {
      lines.push(
        {
          label: 'Stock exchange',
          value: brand?.stockExchange || '',
        },
        {
          label: ['Company Stock', <br key={'br'} />, 'Ticker Symbol'],
          value: brand?.stockSymbol || '',
        }
      )
    }

    return (
      <div className={styles.wrap}>
        <ModalHeader header={businessInformation?.business_name || ''} statusData={statusData} />

        {registrationError && (
          <Alert
            item={{
              type: 'error',
              title: 'Your brand application was rejected.',
              desc: registrationError.message,
            }}
            actions={[
              {
                variant: 'button',
                type: 'primary',
                text: 'Update',
                onClick: updateModalAction,
              },
              {
                variant: 'button',
                type: 'secondary',
                text: 'Learn more',
                onClick: learnMoreModalAction,
              },
            ]}
          />
        )}
        <div className={styles.divider} />

        <ModalInfoBlock lines={lines} />
      </div>
    )
  }
)
